import clsx from 'clsx';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { mq } from '~/lib/helpers/media-query.helper';
import { SalesforceProduct } from '~/lib/salesforce/types';
import { useRaptorProducts } from '~/services/raptor/useRaptorProducts';
import { AspectRatio, Image, Link } from '~/shared/components';
import { CardCarousel } from '~/shared/components/CardCarousel/CardCarousel';
import { useEvents } from '~/shared/hooks/useEvents';
import { _transformProductDetailsToDataLayer } from '~/shared/hooks/useEvents/helpers';
import { GTM_LIST_NAME } from '~/shared/hooks/useEvents/useGTMEvents/models';
import { useFrame } from '~/shared/utils';
import { useTranslation } from '~/shared/utils/translation';
import { ModuleContainer } from '~/templates/blocks/components/ModuleContainer';
import { theme } from '~/theme';
import styles from './ProductsRecentlyViewed.module.scss';

interface CarouselItemProps {
    // TODO(IMC-1671): This should be changed to a Raptor product type.
    product: SalesforceProduct;
    onProductClick?(): void;
    isCondensed?: boolean;
}

const CarouselItem: React.FC<CarouselItemProps> = ({ product, onProductClick, isCondensed }) => (
    <Link
        href={product.url}
        onClick={() => {
            onProductClick?.();

            return true;
        }}
    >
        <div className={clsx(styles.preview, isCondensed && styles.condensed)}>
            <AspectRatio ratio="1/1">
                <div className={styles.imageWrap}>
                    {product.images?.[0] && (
                        <Image
                            {...product.images[0]}
                            objectFit="cover"
                            sizes={`${mq(0, 'md', false)} 50vw, 120px`}
                            layout="fill"
                        />
                    )}
                </div>
            </AspectRatio>
        </div>
    </Link>
);

const RAPTOR_METHOD_RECENTLY_VIEWED_PRODUCTS = 'GetCookieHistory';

type ProductsRecentlyViewedProps = {
    isCondensed?: boolean;
};

export const ProductsRecentlyViewed: React.FC<ProductsRecentlyViewedProps> = ({ isCondensed }) => {
    const { translate } = useTranslation();
    const { data: frame } = useFrame();
    const { productsImpressionsEvent, productClickEvent } = useEvents(frame);
    const { isPlusMember } = useCustomer();

    const {
        data: products,
        isSuccess,
        isError,
    } = useRaptorProducts({
        config: RAPTOR_METHOD_RECENTLY_VIEWED_PRODUCTS,
    });

    const { inView, ref } = useInView({
        triggerOnce: true,
        threshold: 0,
        rootMargin: '0px 0px -500px 0px',
    });

    // TODO(IMC-1671): This should be changed to a Raptor product type.
    const onProductClick = (product: any, position: number) => {
        productClickEvent(
            RAPTOR_METHOD_RECENTLY_VIEWED_PRODUCTS,
            `${GTM_LIST_NAME.RAPTOR} - ${RAPTOR_METHOD_RECENTLY_VIEWED_PRODUCTS}`,
            _transformProductDetailsToDataLayer(product, 1, position, isPlusMember),
        );
    };

    useEffect(() => {
        if (isSuccess && products && products.length > 0 && inView) {
            productsImpressionsEvent(
                RAPTOR_METHOD_RECENTLY_VIEWED_PRODUCTS,
                `${GTM_LIST_NAME.RAPTOR} - ${translate('pdp.viewedProducts')}`,
                // TODO(IMC-1671): This should be changed to a Raptor product type.
                products?.map((product: any, index: number) =>
                    _transformProductDetailsToDataLayer(product, 1, index, isPlusMember),
                ),
            );
        }
    }, [inView, isSuccess]);

    if (isError || !products?.length) return null;

    return (
        <ModuleContainer
            ref={ref}
            spacingTop={isCondensed ? 'small' : 'default'}
            spacingBottom={isCondensed ? 'small' : 'default'}
            backgroundColor={theme.colors.brandBeigeMedium}
        >
            <CardCarousel
                isCondensed={isCondensed}
                headlineVariant={isCondensed ? 'title-XS' : undefined}
                options={{
                    spaceBetween: isCondensed ? 24 : 32,
                    // Ignore changes in spacing on other breakpoints.
                    breakpoints: {},
                }}
                headline={{ tag: 'h2', text: translate('pdp.viewedProducts') }}
                items={products.map((product: any, index: number) => (
                    <CarouselItem
                        key={product.id}
                        product={product}
                        isCondensed={isCondensed}
                        onProductClick={() => onProductClick(product, index)}
                    />
                ))}
            />
        </ModuleContainer>
    );
};
